import { type ReactNode, useEffect } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import cx from 'clsx';
import { Button } from '@repo/design-system-kit/components/button/button';
import { Spinner } from '@repo/design-system-kit/components/spinner/spinner';
import { useSegment } from '@repo/poly-hooks/segment';
import { Steps } from '../steps';
import { QrCode } from '@repo/design-system-kit/components/qr-code/qr-code';
import { BackButtonSimple } from '../../shared/back-button-simple';
import styles from './link-to-mobile.module.css';

export interface LinkToMobileProps {
  isKycRequired: boolean;
  isQesRequired: boolean;
  onSendSMS: () => void;
  onRegenerateLink: () => void;
  onBack: () => void;
  fourthlineRedirectUrl: string;
  mode: string;
  isLoading?: boolean;
}

export function LinkToMobile({
  isKycRequired,
  isQesRequired,
  onRegenerateLink,
  onBack,
  onSendSMS,
  fourthlineRedirectUrl,
  mode,
  isLoading = false,
}: LinkToMobileProps): ReactNode {
  const intl = useIntl();
  const segment = useSegment();

  useEffect(() => {
    onSendSMS();
  }, [onSendSMS]);

  useEffect(() => {
    segment.track(`${mode}_fourthline_link_screen_loaded`);
  }, [mode, segment]);

  return (
    <div data-test-link-to-mobile>
      <BackButtonSimple className="mb-32" data-test-go-back onPress={onBack} />
      <h2 className="heading-section mb-16">
        <FormattedMessage id="identity-verification-kit.fourthline.onboarding.title" />
      </h2>
      <p className="body-standard mb-32">
        <FormattedMessage id="identity-verification-kit.fourthline.onboarding.subtitle" />
      </p>
      <div className={cx('mb-24', styles.insetBox)}>
        {isLoading ? (
          <div className={styles.spinnerContainer}>
            <Spinner color="primary-a" data-testid="spinner" />
          </div>
        ) : (
          <>
            <QrCode className={styles.qrCode} url={fourthlineRedirectUrl} />
            <div className={styles.separator}>
              <div className={styles.separationBar} />
              <span className={styles.separationText} role="separator">
                <FormattedMessage id="identity-verification-kit.fourthline.onboarding.separator" />
              </span>
              <div className={styles.separationBar} />
            </div>
            <Button data-test-send-sms-button onPress={onSendSMS} type="button" variant="secondary">
              <FormattedMessage id="identity-verification-kit.fourthline.onboarding.send-link" />
            </Button>
          </>
        )}
      </div>
      {isKycRequired && isQesRequired ? (
        <Steps
          className="mb-24"
          data-test-kyc-and-qes-guidance
          steps={[
            intl.formatMessage({
              id: 'identity-verification-kit.fourthline.onboarding.steps.instruction-id',
            }),
            intl.formatMessage({
              id: 'identity-verification-kit.fourthline.onboarding.steps.instruction-sign',
            }),
          ]}
        />
      ) : null}
      {isKycRequired && !isQesRequired ? (
        <p className="body-standard mb-24" data-test-kyc-guidance>
          <FormattedMessage id="identity-verification-kit.fourthline.onboarding.kyc.guidance" />
        </p>
      ) : null}
      {isQesRequired && !isKycRequired ? (
        <p className="body-standard mb-24" data-test-qes-guidance>
          <FormattedMessage id="identity-verification-kit.fourthline.onboarding.qes.guidance" />
        </p>
      ) : null}
      <div className="mb-32">
        <span className="caption">
          <FormattedMessage id="identity-verification-kit.fourthline.onboarding.not-working" />
        </span>
        <button
          className={cx('caption-link', styles.regenerateLink)}
          data-test-regenerate-link
          onClick={onRegenerateLink}
          type="button"
        >
          <FormattedMessage id="identity-verification-kit.fourthline.onboarding.generate-new-link" />
        </button>
      </div>
    </div>
  );
}
